// Pure logic extracted from app/api/alpha-zone-tracker/route.ts.
// Two backtest modes: 'strict' (snapshot exists at the anchor date) vs
// 'whatif' (today's Alpha basket retro-priced from the cache — biased,
// flagged in the response). Helpers for both, plus the per-stock and
// summary stat computations, live here.

import { compositeScore, isAlphaZoneEligible } from './composite-score';
import { BASKET_SIZE } from './alpha-basket';

export interface BasketEntry {
  symbol: string;
  name?: string;
  sector?: string;
  rank?: number;
  price: number;
  /** Target portfolio weight assigned at the leg's last rebalance. Optional —
   *  legacy snapshots and equal-weight baskets won't have it. The UI falls
   *  back to 1 / basket.length when missing. */
  weight?: number;
}

export interface AnchorSnapshot {
  date: string;
  modelVersion?: string;
  alpha_zone: BasketEntry[];
  beta_zone?: BasketEntry[]; // legacy: pre-2026-05-04 snapshots split top-20
  benchmark: { SPY: number | null; SPX: number | null };
}

export interface ScoresFile {
  updatedAt?: string;
  stocks?: Array<{
    symbol: string;
    name?: string;
    sector?: string;
    qualityScore?: number | null;
    /** Production Q_blend = 0.75·peer-z + 0.25·universe-z absolute. When present,
     *  basket selection uses this; falls back to qualityScore (peer-z) when null. */
    qualityScoreBlended?: number | null;
    valuationScore?: number | null;
    /** Production V_blend = 0.75·peer-z + 0.25·universe-z absolute (Definition C).
     *  When present, basket selection uses this; falls back to peer-z when null
     *  (banks, insufficient EPS history). Steve directive 2026-05-08. */
    valuationScoreBlended?: number | null;
    price?: number | null;
  }>;
}

export interface PriceRow { date: string; price: number }
export interface DailyCache {
  from?: string;
  to?: string;
  histories?: Record<string, PriceRow[]>;
}

/** Today's Alpha Zone (top 10 by composite, gated to BUY tier composite ≥ 1.5). */
export function todaysAlphaBasket(scores: ScoresFile | null): BasketEntry[] {
  const stocks = scores?.stocks ?? [];
  const ranked = stocks
    .filter((s) => isAlphaZoneEligible((s.qualityScoreBlended ?? s.qualityScore) ?? null, (s.valuationScoreBlended ?? s.valuationScore) ?? null))
    .map((s) => ({
      symbol: s.symbol,
      name: s.name,
      sector: s.sector,
      composite: compositeScore((s.qualityScoreBlended ?? s.qualityScore) ?? null, (s.valuationScoreBlended ?? s.valuationScore) ?? null) ?? -Infinity,
      price: s.price ?? 0,
    }))
    .sort((a, b) => b.composite - a.composite)
    .slice(0, BASKET_SIZE);
  return ranked.map((r, i) => ({
    symbol: r.symbol,
    name: r.name,
    sector: r.sector,
    rank: i + 1,
    price: r.price,
  }));
}

/** Pick price on or before targetDate from a date-ascending history. */
export function priceOnOrBefore(history: PriceRow[] | undefined, targetDate: string): number | null {
  if (!history || history.length === 0) return null;
  let last: number | null = null;
  for (const r of history) {
    if (r.date > targetDate) break;
    if (Number.isFinite(r.price)) last = r.price;
  }
  return last;
}

/** Build the basket for the what-if (no snapshot) mode by retro-pricing today's basket. */
export function buildWhatIfBasket(
  scores: ScoresFile | null,
  cache: DailyCache | null,
  anchorDate: string,
): BasketEntry[] {
  const todays = todaysAlphaBasket(scores);
  const histories = cache?.histories ?? {};
  return todays.map((b) => {
    const anchorPrice = priceOnOrBefore(histories[b.symbol], anchorDate) ?? b.price;
    return { ...b, price: anchorPrice };
  });
}

/** Per-stock return from anchor → current, plus lifetime context: when each
 *  name entered the Alpha Zone, days since, and rolling %change windows
 *  (today / MTD / YTD / since addition). The lifetime fields are independent
 *  of the selected anchor — they describe the holding itself. */
export interface PerStockRow {
  symbol: string;
  name: string | null;
  sector: string | null;
  rank: number | null;
  anchorPrice: number;
  currentPrice: number | null;
  pctReturn: number | null;          // anchor → current (varies with the picker)
  enteredOn: string | null;          // ISO date the stock first entered Alpha Zone
  daysIn: number | null;             // calendar days since enteredOn
  pctToday: number | null;           // yesterday close → current price
  pctMtd: number | null;             // last-day-of-prior-month close → current
  pctYtd: number | null;             // last-day-of-prior-year close → current
  pctSinceAddition: number | null;   // enteredOn close → current
  /** Target portfolio weight from the conviction rule (or null for equal-weight legacy
   *  baskets — UI substitutes 1/basket.length in that case). */
  weight: number | null;
}

interface ZoneChangeRow { symbol: string; enteredOn: string }

/** Pick the most-recent price on or before targetDate from a date-ascending
 *  history. Returns null if the history doesn't reach back that far. */
function closestPriceAround(history: PriceRow[] | undefined, targetDate: string): number | null {
  if (!history || history.length === 0) return null;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].date <= targetDate) return history[i].price;
  }
  return null;
}

/** Same as closestPriceAround but returns the actual matched date too —
 *  callers can detect a stale-cache fallback (asking for "yesterday" but the
 *  cache only goes back 2 weeks). Used for the %Today column so a stale
 *  cache can't silently show a multi-week move as today's. */
function closestPricedAround(
  history: PriceRow[] | undefined,
  targetDate: string,
): { price: number; date: string } | null {
  if (!history || history.length === 0) return null;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].date <= targetDate) return { price: history[i].price, date: history[i].date };
  }
  return null;
}

function dayDiff(a: string, b: string): number {
  return Math.abs(Math.round((Date.parse(a) - Date.parse(b)) / 86_400_000));
}

/** ISO YYYY-MM-DD arithmetic (no DST issues — uses UTC). */
function previousDay(iso: string): string {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
/** Last day of the month before today's month — May 5 → April 30. */
function priorMonthEnd(today: string): string {
  const d = new Date(today + 'T12:00:00Z');
  d.setUTCDate(0); // day=0 rolls back to the last day of the prior month
  return d.toISOString().slice(0, 10);
}
/** Last day of the year before today's year — 2026-05-05 → 2025-12-31. */
function priorYearEnd(today: string): string {
  const d = new Date(today + 'T12:00:00Z');
  return `${d.getUTCFullYear() - 1}-12-31`;
}
function daysBetween(from: string, to: string): number {
  return Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000));
}

export function computePerStock(
  basket: BasketEntry[],
  scores: ScoresFile | null,
  cache: DailyCache | null,
  zoneChanges: { alphaZone?: { current?: ZoneChangeRow[] } } | null,
  todayIso: string,
): PerStockRow[] {
  const currentPrices = new Map<string, number>();
  for (const s of scores?.stocks ?? []) {
    if (typeof s.price === 'number' && Number.isFinite(s.price) && s.price > 0) {
      currentPrices.set(s.symbol, s.price);
    }
  }

  const enteredBySymbol = new Map<string, string>();
  for (const r of zoneChanges?.alphaZone?.current ?? []) enteredBySymbol.set(r.symbol, r.enteredOn);

  const histories = cache?.histories ?? {};
  const yesterday = previousDay(todayIso);
  const monthEnd = priorMonthEnd(todayIso);
  const yearEnd = priorYearEnd(todayIso);

  return basket.map((b) => {
    const cur = currentPrices.get(b.symbol);
    const pctReturn = cur != null && b.price > 0 ? (cur - b.price) / b.price : null;

    const enteredOn = enteredBySymbol.get(b.symbol) ?? null;
    const daysIn = enteredOn ? daysBetween(enteredOn, todayIso) : null;

    const hist = histories[b.symbol];
    // %Today must compare current vs an actual prior-trading-day close —
    // if the cache is stale (e.g. last entry is 2 weeks back) the move
    // would otherwise read as "today's." Gate at 4 calendar days.
    const yMatch = closestPricedAround(hist, yesterday);
    const pxYesterday = yMatch != null && dayDiff(yMatch.date, todayIso) <= 4 ? yMatch.price : null;
    const pxMonthEnd = closestPriceAround(hist, monthEnd);
    const pxYearEnd = closestPriceAround(hist, yearEnd);
    const pxEntered = enteredOn ? closestPriceAround(hist, enteredOn) : null;

    const pct = (anchor: number | null): number | null =>
      cur != null && anchor != null && anchor > 0 ? (cur - anchor) / anchor : null;

    return {
      symbol: b.symbol,
      name: b.name ?? null,
      sector: b.sector ?? null,
      rank: b.rank ?? null,
      anchorPrice: b.price,
      currentPrice: cur ?? null,
      pctReturn,
      enteredOn,
      daysIn,
      pctToday: pct(pxYesterday),
      pctMtd: pct(pxMonthEnd),
      pctYtd: pct(pxYearEnd),
      pctSinceAddition: pct(pxEntered),
      weight: b.weight ?? null,
    };
  });
}

/** Equal-weighted average of a per-stock numeric field, ignoring nulls. */
function basketMean(rows: PerStockRow[], pick: (r: PerStockRow) => number | null): number | null {
  const vals = rows.map(pick).filter((v): v is number => v != null && Number.isFinite(v));
  if (vals.length === 0) return null;
  return vals.reduce((s, x) => s + x, 0) / vals.length;
}

export interface BasketSummary {
  alphaReturn: number;
  alphaReturnMedian: number;
  hitRate: number;
  spyAnchor: number | null;
  spyCurrent: number | null;
  spyReturn: number | null;
  edge: number | null;
  edgeMedian: number | null;
  coverage: number;
  // Lifetime equal-weighted basket stats (independent of the selected anchor):
  basketPctToday: number | null;
  basketPctMtd: number | null;
  basketPctYtd: number | null;
  basketPctSinceAddition: number | null;
  avgDaysIn: number | null;
}

export function computeSummary(
  perStock: PerStockRow[],
  spyAnchor: number | null,
  spyCurrent: number | null,
): BasketSummary {
  const valid = perStock.filter((p) => p.pctReturn != null);
  const validReturns = valid.map((p) => p.pctReturn ?? 0);
  const alphaReturn = validReturns.length > 0
    ? validReturns.reduce((s, x) => s + x, 0) / validReturns.length
    : 0;
  const sorted = [...validReturns].sort((a, b) => a - b);
  const alphaReturnMedian = sorted.length === 0 ? 0
    : sorted.length % 2 === 1
      ? sorted[Math.floor(sorted.length / 2)]
      : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
  const hitRate = valid.length > 0
    ? valid.filter((p) => (p.pctReturn ?? 0) > 0).length / valid.length
    : 0;
  const spyReturn = spyAnchor != null && spyCurrent != null && spyAnchor > 0
    ? (spyCurrent - spyAnchor) / spyAnchor
    : null;
  return {
    alphaReturn,
    alphaReturnMedian,
    hitRate,
    spyAnchor,
    spyCurrent,
    spyReturn,
    edge: spyReturn != null ? alphaReturn - spyReturn : null,
    edgeMedian: spyReturn != null ? alphaReturnMedian - spyReturn : null,
    coverage: valid.length / Math.max(1, perStock.length),
    basketPctToday: basketMean(perStock, (r) => r.pctToday),
    basketPctMtd: basketMean(perStock, (r) => r.pctMtd),
    basketPctYtd: basketMean(perStock, (r) => r.pctYtd),
    basketPctSinceAddition: basketMean(perStock, (r) => r.pctSinceAddition),
    avgDaysIn: basketMean(perStock, (r) => r.daysIn),
  };
}
